// Locale-aware formatting fed by the active i18n locale (set from I18nContext).
// Does NOT change stored values or business logic — presentation only.
import en from "@/i18n/en.json";
import es from "@/i18n/es.json";

const DICTS: Record<string, Record<string, string>> = { en, es };
let currentLocale = "en";
const bcp = (l: string) => (l === "es" ? "es-419" : "en-US");

export function setFormatLocale(l: string) {
  currentLocale = l || "en";
}

// Module-level translator for imperative contexts (toasts, confirm dialogs) that
// fire on events and are not reactive — mirrors the I18nContext t() lookup + fallback.
export function tl(key: string, vars?: Record<string, string | number>): string {
  const dict = DICTS[currentLocale] || DICTS.en;
  let s = dict[key] ?? DICTS.en[key] ?? key;
  if (vars) for (const k of Object.keys(vars)) s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(vars[k]));
  return s;
}

export function fmtNum(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  return new Intl.NumberFormat(bcp(currentLocale)).format(v);
}

export function fmtMoney(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  return "$" + new Intl.NumberFormat(bcp(currentLocale)).format(Math.round(v));
}

export function fmtDate(d: string | number | Date | null | undefined): string {
  if (d == null) return "—";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return new Intl.DateTimeFormat(bcp(currentLocale), { year: "numeric", month: "short", day: "numeric" }).format(dt);
}

export function fmtDateTime(d: string | number | Date | null | undefined): string {
  if (d == null) return "—";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return new Intl.DateTimeFormat(bcp(currentLocale), { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(dt);
}

export function fmtTime(d: string | number | Date | null | undefined): string {
  if (d == null) return "—";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return new Intl.DateTimeFormat(bcp(currentLocale), { hour: "2-digit", minute: "2-digit" }).format(dt);
}

export function localeTag(): string { return bcp(currentLocale); }

// Localizes short status/enum tokens coming from the backend or hardcoded literals,
// without changing the underlying stored value. Falls back to the token itself.
const STATUS_EN: Record<string, string> = {
  ok: "OK", attention: "Attention", not_configured: "Not Configured", configured: "Configured",
  test_mode: "Test mode", live: "Live", protected: "Protected", stale: "Stale",
  queued: "Queued", leased: "Leased", done: "Done", dead_letter: "Dead-letter",
  skipped_module_off: "Skipped (module off)", processing: "Processing", completed: "Completed",
  failed: "Failed", delivered: "Delivered", paid: "Paid", void: "Void", open: "Open",
  pending: "Pending", sent: "Sent", running: "Running", active: "Active", inactive: "Inactive",
  draft: "Draft", published: "Published", superseded: "Superseded", cancelled: "Cancelled",
  approved: "Approved", submitted: "Submitted", enabled: "Enabled", off: "Off",
  // priority / severity / confidence
  high: "High", medium: "Medium", low: "Low", urgent: "Urgent", normal: "Normal", critical: "Critical",
  info: "Info", warn: "Warning", warning: "Warning", good: "Good",
  // schedule / shift / request states
  accepted: "Accepted", denied: "Denied", rejected: "Rejected", callout: "Callout",
  committed: "Committed", rolled_back: "Rolled back", scheduled: "Scheduled",
  resolved: "Resolved", monitoring: "Monitoring", closed: "Closed", reopened: "Reopened",
  // certification
  valid: "Valid", expired: "Expired", expiring: "Expiring", not_certified: "Not certified",
  // intelligence states
  degraded: "Degraded", healthy: "Healthy", error: "Error", clear: "Clear",
  // exception / adjustment categories
  tech_outage: "Tech outage", weather: "Weather", event: "Event", promo: "Promotion",
  holiday: "Holiday", remodel: "Remodel", other: "Other", system: "System",
};
export function stat(s?: string | null): string {
  if (s == null || s === "") return "—";
  const label = STATUS_EN[String(s).toLowerCase()] || String(s);
  return tl(label);
}
